import React from 'react';
import { useQuery } from 'react-query';
import styles from './YearsList.module.less';
import { useFilters } from '../../hooks/useFilters';
import { fetchFilteredAllContent } from '../../utils/api';
import { Icon } from '../shared/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../stores/themeStore';
import { useYearsStore } from '../../stores/useYearStore';

const YearsList: React.FC = () => {
  const { activeYear, scrollToYear, showMobileFilter, setShowMobileFilter } =
    useYearsStore();
  const {
    filters,
    selectedCategory,
    selectedSubcategories,
  } = useFilters();

  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const { data: availableContentYears } = useQuery(
    ['availableYears', selectedCategory, selectedSubcategories, filters],
    async () => {
      const response = await fetchFilteredAllContent({
        pageParam: 1,
        categoryId: selectedCategory,
        subcategoryIds: selectedSubcategories,
        filters,
        selectedYears: [],
      });

      const years = new Set<number>();
      response.data.forEach((content) => {
        if (content.years && Array.isArray(content.years)) {
          content.years.forEach((yearObj) => {
            if (yearObj.years_id && typeof yearObj.years_id.year === 'number') {
              years.add(yearObj.years_id.year);
            }
          });
        }
      });

      return years;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const handleButtonYearClick = (year: number) => {
    setShowMobileFilter(!showMobileFilter); 
    scrollToYear(year);
  };

  const handleYearClick = () => {
    setShowMobileFilter(!showMobileFilter); 
  };

  return (
    <div className={styles['yearsList']}>
      <div className={styles['yearsList__mobile']}>
        <button
          onClick={handleYearClick}
          className={`${styles.yearsList__filterButton} ${
            theme !== 'light' && styles.yearsList__filterButton__dark
          }`}
        >
          <span>{activeYear ? activeYear : new Date().getFullYear()}</span>
        </button>

        {showMobileFilter && (
          <div className={styles['yearsList__mobile-overlay']}>
            <div className={styles['yearsList__mobile-header']}>
              <span>{t('Year')}</span>
              <button
                onClick={() => setShowMobileFilter(false)}
                className={styles['yearsList__close-button']}
              >
                <Icon id="close-dropdown" className={styles.icon} />
              </button>
            </div>
            <div className={styles['yearsList__mobile-content']}>
              {Array.from(availableContentYears || [])
                .reverse()
                .map((year) => (
                  <li
                    key={year}
                    onClick={() => handleButtonYearClick(year)}
                    className={`${styles['yearsList__dropdown-item']} ${
                      !availableContentYears?.has(year)
                        ? styles['yearsList__dropdown-item--disabled']
                        : ''
                    }`}
                  >
                    <button
                      className={`${styles['yearsList__dropdown-button']} ${
                        activeYear === year
                          ? styles['yearsList__button--active']
                          : ''
                      }`}
                      disabled={!availableContentYears?.has(year)}
                    >
                      {year}
                    </button>
                  </li>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles['yearsList__buttons']}>
        {Array.from(availableContentYears || [])
          .reverse()
          .map((year) => (
            <button
              key={year}
              onClick={() => scrollToYear(year)}
              className={`${styles['yearsList__button']} ${
                activeYear === year ? styles['yearsList__button--active'] : ''
              } ${
                !availableContentYears?.has(year)
                  ? styles['yearsList__button--disabled']
                  : ''
              }`}
              disabled={!availableContentYears?.has(year)}
            >
              {year}
            </button>
          ))}
      </div>
    </div>
  );
};

export default YearsList;


